import request from "../utils/request";

export type StudySourceConnectionType = 0 | 1;

export interface StudySourceItem {
  id: string;
  studyId: string;
  studyName: string;
  studyCode: string;
  connectionType: StudySourceConnectionType;
  /** Teams Channel 链接，仅 connectionType=0（Teams）非空。 */
  sourceUrl: string;
  /** 由后端统一 Parser 从 Teams URL 路径派生；不是 Graph 实时 displayName。 */
  channelNameFromUrl?: string;
  /** SharePoint Site 名；Teams / SharePoint 均由用户填写。 */
  sourceSite: string;
  /** SharePoint 文档库；Teams / SharePoint 均可填，空表示所有业务库。 */
  sourceDrive: string;
  /** 由后端按运行时 SharePoint host 生成的 Site 浏览器地址。 */
  sharePointSiteUrl?: string;
  includedPaths: string[];
  excludedPaths: string[];
  remark?: string;
  agentTips?: string;
  userListSync: boolean;
  userScope: 0 | 1;
  docCategoryIds: string[];
  docTagBindings: StudySourceTagBinding[];
  sourceCode?: string;
}

export interface StudySourceTagBinding {
  tagId: string;
  presetValues: string[];
}

export interface StudySourceSaveInput {
  studyId: string;
  connectionType: StudySourceConnectionType;
  /** Teams：Teams URL；SharePoint 下不传。 */
  sourceUrl?: string;
  /** Teams / SharePoint：Source Site（必填）。 */
  sourceSite?: string;
  /** Teams / SharePoint：可选的 Source Drive。 */
  sourceDrive?: string;
  includedPaths: string[];
  excludedPaths: string[];
  remark?: string;
  agentTips?: string;
  userListSync: boolean;
  userScope: 0 | 1;
  docCategoryIds: string[];
  docTagBindings: StudySourceTagBinding[];
}

interface StudySourceItemWire {
  ID?: string;
  id?: string;
  StudyId?: string;
  studyId?: string;
  StudyName?: string;
  studyName?: string;
  StudyCode?: string;
  studyCode?: string;
  ConnectionType?: number;
  connectionType?: number;
  SourceUrl?: string;
  sourceUrl?: string;
  ChannelNameFromUrl?: string | null;
  channelNameFromUrl?: string | null;
  SourceSite?: string | null;
  sourceSite?: string | null;
  SourceDrive?: string | null;
  sourceDrive?: string | null;
  SharePointSiteUrl?: string | null;
  sharePointSiteUrl?: string | null;
  IncludedPaths?: unknown;
  includedPaths?: unknown;
  ExcludedPaths?: unknown;
  excludedPaths?: unknown;
  Remark?: string | null;
  remark?: string | null;
  AgentTips?: string | null;
  agentTips?: string | null;
  UserListSync?: boolean;
  userListSync?: boolean;
  UserScope?: number;
  userScope?: number;
  DocCategoryIds?: unknown;
  docCategoryIds?: unknown;
  DocTagBindings?: unknown;
  docTagBindings?: unknown;
  SourceCode?: string | null;
  sourceCode?: string | null;
}

interface StudySourceListWire {
  Count?: number;
  count?: number;
  Data?: StudySourceItemWire[];
  data?: StudySourceItemWire[];
}

export interface StudySourceListResponse {
  count: number;
  data: StudySourceItem[];
}

export interface StudySourceMember {
  email: string;
  displayName: string;
  roles: string;
}

interface StudySourceMemberWire {
  Email?: string | null;
  email?: string | null;
  DisplayName?: string | null;
  displayName?: string | null;
  Roles?: string | null;
  roles?: string | null;
}

interface StudySourceMemberListWire {
  Count?: number;
  count?: number;
  Data?: StudySourceMemberWire[];
  data?: StudySourceMemberWire[];
}

export interface StudySourceMemberListResponse {
  count: number;
  data: StudySourceMember[];
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}

function normalizeTagBindings(value: unknown): StudySourceTagBinding[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const binding = item as Record<string, unknown>;
      const tagId = String(binding.TagId ?? binding.tagId ?? "");
      return {
        tagId,
        presetValues: normalizeStringArray(binding.PresetValues ?? binding.presetValues),
      };
    })
    .filter((binding) => !!binding.tagId);
}

export function normalizeStudySource(item: StudySourceItemWire): StudySourceItem {
  const rawConnectionType = Number(item.ConnectionType ?? item.connectionType ?? 0);

  return {
    id: String(item.ID ?? item.id ?? ""),
    studyId: String(item.StudyId ?? item.studyId ?? ""),
    studyName: item.StudyName ?? item.studyName ?? "",
    studyCode: item.StudyCode ?? item.studyCode ?? "",
    connectionType: rawConnectionType === 1 ? 1 : 0,
    sourceUrl: item.SourceUrl ?? item.sourceUrl ?? "",
    channelNameFromUrl: item.ChannelNameFromUrl ?? item.channelNameFromUrl ?? "",
    sourceSite: item.SourceSite ?? item.sourceSite ?? "",
    sourceDrive: item.SourceDrive ?? item.sourceDrive ?? "",
    sharePointSiteUrl: item.SharePointSiteUrl ?? item.sharePointSiteUrl ?? "",
    includedPaths: normalizeStringArray(item.IncludedPaths ?? item.includedPaths),
    excludedPaths: normalizeStringArray(item.ExcludedPaths ?? item.excludedPaths),
    remark: item.Remark ?? item.remark ?? undefined,
    agentTips: item.AgentTips ?? item.agentTips ?? undefined,
    userListSync: Boolean(item.UserListSync ?? item.userListSync),
    userScope: Number(item.UserScope ?? item.userScope) === 1 ? 1 : 0,
    docCategoryIds: normalizeStringArray(item.DocCategoryIds ?? item.docCategoryIds),
    docTagBindings: normalizeTagBindings(item.DocTagBindings ?? item.docTagBindings),
    sourceCode: item.SourceCode ?? item.sourceCode ?? undefined,
  };
}

const ADMIN_BASE = "/project/sourcepathstudy";
const NO_REQUEST_TIMEOUT = { timeout: 0 } as const;

function toWire(input: StudySourceSaveInput) {
  const isSharePoint = input.connectionType === 1;
  return {
    StudyId: input.studyId,
    ConnectionType: input.connectionType,
    SourceUrl: isSharePoint ? null : (input.sourceUrl ?? ""),
    SourceSite: input.sourceSite ?? "",
    SourceDrive: input.sourceDrive ?? null,
    IncludedPaths: input.includedPaths,
    ExcludedPaths: input.excludedPaths,
    Remark: input.remark,
    AgentTips: input.agentTips,
    UserListSync: input.userListSync,
    UserScope: input.userScope,
    DocCategoryIds: input.docCategoryIds,
    DocTagBindings: input.docTagBindings.map((binding) => ({
      TagId: binding.tagId,
      PresetValues: binding.presetValues,
    })),
  };
}

/** Admin Study Source Settings 列表。 */
export async function listStudySources(): Promise<StudySourceListResponse> {
  const response = await request.get<StudySourceListWire>(`${ADMIN_BASE}/list`);
  const wire = response.data ?? {};
  const data = (wire.Data ?? wire.data ?? []).map(normalizeStudySource);
  return { count: Number(wire.Count ?? wire.count ?? data.length), data };
}

export async function listStudySourceMembers(
  sourceId: string,
): Promise<StudySourceMemberListResponse> {
  const response = await request.get<StudySourceMemberListWire>(
    `${ADMIN_BASE}/${encodeURIComponent(sourceId)}/members`,
  );
  const wire = response.data ?? {};
  const data = (wire.Data ?? wire.data ?? [])
    .map((member) => ({
      email: (member.Email ?? member.email ?? "").trim(),
      displayName: (member.DisplayName ?? member.displayName ?? "").trim(),
      roles: (member.Roles ?? member.roles ?? "").trim(),
    }))
    .filter((member) => !!member.email);
  return { count: Number(wire.Count ?? wire.count ?? data.length), data };
}

export async function createStudySource(input: StudySourceSaveInput): Promise<StudySourceItem> {
  const response = await request.post<StudySourceItemWire>(
    `${ADMIN_BASE}/create`,
    toWire(input),
    NO_REQUEST_TIMEOUT,
  );
  return normalizeStudySource(response.data);
}

export async function updateStudySource(
  id: string,
  input: StudySourceSaveInput,
): Promise<StudySourceItem> {
  const response = await request.put<StudySourceItemWire>(
    `${ADMIN_BASE}/update`,
    {
      ID: id,
      ...toWire(input),
    },
    NO_REQUEST_TIMEOUT,
  );
  return normalizeStudySource(response.data);
}

export function deleteStudySource(id: string) {
  return request.delete(`${ADMIN_BASE}/${encodeURIComponent(id)}`, NO_REQUEST_TIMEOUT);
}

export async function getStudySourceGlobalExcludedKeywords(): Promise<string[]> {
  const response = await request.get<{ Keywords?: string[]; keywords?: string[] }>(
    `${ADMIN_BASE}/globalexcludedkeywords`,
  );
  return response.data?.Keywords ?? response.data?.keywords ?? [];
}

export async function updateStudySourceGlobalExcludedKeywords(
  keywords: string[],
): Promise<string[]> {
  const response = await request.put<{ Keywords?: string[]; keywords?: string[] }>(
    `${ADMIN_BASE}/globalexcludedkeywords`,
    { Keywords: keywords },
  );
  return response.data?.Keywords ?? response.data?.keywords ?? [];
}

/** 获取当前 CRA / CSM 被授权 Study 下配置的真实 Study Source。 */
export async function listMyStudySources(): Promise<StudySourceListResponse> {
  const response = await request.get<StudySourceListWire>("/project/askscope/studysources");
  const wire = response.data ?? {};
  const data = (wire.Data ?? wire.data ?? []).map(normalizeStudySource);

  return {
    count: Number(wire.Count ?? wire.count ?? data.length),
    data,
  };
}
